import React, { useState, useEffect } from "react";
import { Table, Button, Spin, message, Modal } from "antd";
import { FaTrash, FaEye, FaDownload, FaSitemap } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";

const { confirm } = Modal;

const SiteMap = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sitemaps, setSitemaps] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSitemapId, setSelectedSitemapId] = useState(null);

  const BASE_URL = "https://flagshopscanada.com";

  const fetchSitemaps = async () => {
    try {
      setLoading(true);
      const response = await apiHandle.get("admin/get-all-sitemap");
      setSitemaps(response.data.data.sitemap);
    } catch (error) {
      console.error("Error fetching sitemaps:", error);
      message.error("Failed to load sitemaps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSitemaps();
  }, []);

  const openDeleteModal = (id) => {
    setSelectedSitemapId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiHandle.delete(`admin/delete-sitemap/${selectedSitemapId}`);
      message.success("Sitemap deleted successfully");
      fetchSitemaps();
    } catch (error) {
      console.error("Error deleting sitemap:", error);
      message.error("Failed to delete sitemap");
    } finally {
      setShowDeleteModal(false);
      setSelectedSitemapId(null);
    }
  };

  const handleView = (url) => {
    window.open(url, "_blank");
  };

  const handleDownload = async (id, filename) => {
    try {
      const response = await apiHandle.get(`admin/download-sitemap/${id}`, {
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "sitemap.xml");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading sitemap:", error);
      message.error("Failed to download sitemap");
    }
  };

  // Static routes and custom slugs from your existing component
  const staticRoutes = [
    "/",
    "/shop",
    "/cart",
    "/custom-quote",
    "/custom-product",
    "/checkout",
    "/wishlist",
    "/contact",
    "/stores",
    "/order-status",
    "/custom-flag",
    "/thankyou",
    "/order-confirmation",
    "/sign-in",
    "/sign-up",
  ];

  const customSlugs = [
    "/custom/car-flag",
    "/custom/feather-banner",
    "/custom/custom-paper-flag",
    "/custom/custom-embroidery",
    "/custom/podium-banners",
    "/custom/applique-sew-flags",
    "/custom/custom-flags",
    "/custom/street-banner",
    "/custom/pennants-and-Burgee",
    "/custom/outdoor-banner-and-signage-displays",
    "/custom/over-the-street-banners",
    "/custom/indoor-banner-and-signage-displays",
    "/custom/fence-wraps",
  ];

  // Fetch dynamic routes from APIs
  const fetchDynamicRoutes = async () => {
    try {
      const [products, stores, categories] = await Promise.all([
        fetch(
          "https://flag-shop-0d5fe80ac3cf.herokuapp.com/shop?limit=1000"
        ).then((res) => res.json()),
        fetch(
          "https://flag-shop-0d5fe80ac3cf.herokuapp.com/all-stores?limit=1000"
        ).then((res) => res.json()),
        fetch(
          "https://flag-shop-0d5fe80ac3cf.herokuapp.com/all-categories?limit=1000"
        ).then((res) => res.json()),
      ]);

      const productRoutes =
        products.data?.products.map((p) => `/product/${p.slug}`) || [];
      const storeRoutes =
        stores.data?.stores.map((s) => `/stores/${s.slug}`) || [];
      const categoryRoutes =
        categories?.data?.map((c) => `/category/${c.slug}`) || [];

      return [...storeRoutes, ...productRoutes, ...categoryRoutes];
    } catch (err) {
      console.error("Fetch failed:", err);
      return [];
    }
  };

  // Generate sitemap XML and save it
  const handleGenerateSitemap = async () => {
    try {
      setGenerating(true);
      message.loading("Generating sitemap...");

      // Fetch dynamic routes
      const dynamicRoutes = await fetchDynamicRoutes();
      const allRoutes = [...staticRoutes, ...customSlugs, ...dynamicRoutes];
      const today = new Date().toISOString().split("T")[0];

      let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`;

      allRoutes.forEach((route) => {
        let priority = "0.8";
        let changefreq = "weekly";

        if (route === "/sign-in" || route === "/sign-up") {
          priority = "0.5";
          changefreq = "monthly";
        } else if (route.includes("/custom/")) {
          priority = "0.8";
        } else if (route.includes("/product/") || route.includes("/stores/")) {
          priority = "0.7";
        }

        xml += `
  <url>
    <loc>${BASE_URL}${route}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
      });

      xml += "\n</urlset>";

      // Save to backend via API
      const formData = new FormData();
      const blob = new Blob([xml], { type: "application/xml" });
      formData.append("file", blob, `sitemap-${today}.xml`);
      formData.append("name", `sitemap-${today}.xml`);

      await apiHandle.post("admin/add-sitemap", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("Sitemap generated and saved successfully");
      fetchSitemaps(); // Refresh list after generating
    } catch (error) {
      console.error("Error generating sitemap:", error);
      message.error("Failed to generate sitemap");
    } finally {
      setGenerating(false);
    }
  };

  const columns = [
    {
      title: "Sitemap Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Generated Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-3">
          <Button
            type="primary"
            icon={<FaDownload />}
            onClick={() => handleView(record.url)}
            size="small"
          />
          <Button
            type="danger"
            icon={<FaTrash />}
            onClick={() => openDeleteModal(record._id)}
            size="small"
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Sitemap Management</h1>
        <Button
          type="primary"
          icon={<FaSitemap />}
          onClick={handleGenerateSitemap}
          loading={generating}
        >
          Generate Sitemap
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={sitemaps}
        loading={loading}
        rowKey="_id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />
      <Modal
        title="Delete Sitemap"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        okText="Yes, Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to delete this sitemap? This action cannot be
          undone.
        </p>
      </Modal>

      {loading && (
        <div className="text-center mt-4">
          <Spin size="large" />
          <p className="mt-2 text-gray-600">Loading sitemaps...</p>
        </div>
      )}
    </div>
  );
};

export default SiteMap;
